import { createContext, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
    attachAudioPlayerMediaSession,
    attachAudioPlayerPersistence,
    createAudioPlayer,
    createLocalStoragePersistenceAdapter,
    type AudioPlayer,
    type AudioPlayerState,
    type AudioSource,
} from "@yetric/audioplayer";
import { listeningStore, playerStore } from "@/context/stores";

export interface AudioState {
    audio: AudioSource | undefined;
    isPlaying: boolean;
    setAudio: (source: AudioSource, startAt?: number) => void;
    play: () => void;
    pause: () => void;
    seek: (time?: number) => number;
    duration: () => number;
    rate: (speed?: number) => number;
    unload: () => void;
}

export const AudioContext = createContext<AudioState>({} as AudioState);

const AUDIO_PLAYER_PERSISTENCE_KEY = "hp_audioplayer_state";
const PROGRESS_SAVE_INTERVAL_MS = 5000;

export const AudioProvider = ({ children }: { children: ReactNode }) => {
    const [player] = useState<AudioPlayer>(() => createAudioPlayer());
    const [playerState, setPlayerState] = useState(() => player.getState());
    // True from loading an episode until its saved position has been applied, so the
    // position 0 reported meanwhile does not overwrite the one we are resuming from
    const resuming = useRef(false);

    useEffect(() => {
        // Remember where the listener is in the episode that is loaded
        let lastSaved = 0;
        const saveProgress = (state: AudioPlayerState, force = false) => {
            const { currentEpisode, currentPodcast } = playerStore;
            if (resuming.current || !playerStore.active || state.currentSourceId !== String(currentEpisode.id)) {
                return;
            }
            const now = Date.now();
            if (!force && now - lastSaved < PROGRESS_SAVE_INTERVAL_MS) {
                return;
            }
            lastSaved = now;
            listeningStore.record(currentEpisode, currentPodcast, state.currentTime, state.duration);
        };

        const persistence = attachAudioPlayerPersistence(player, {
            adapter: createLocalStoragePersistenceAdapter(AUDIO_PLAYER_PERSISTENCE_KEY),
            autoRestore: false,
        });
        const mediaSession = attachAudioPlayerMediaSession(player);
        const unsubscribe = player.subscribe((nextState) => {
            setPlayerState(nextState);
            playerStore.setPlayingState(nextState.status === "playing");
        });
        const unsubscribeTime = player.on("timeupdate", ({ state }) => saveProgress(state));
        const unsubscribeEnded = player.on("ended", ({ state }) => {
            const { currentEpisode, currentPodcast } = playerStore;
            if (state.currentSourceId === String(currentEpisode.id)) {
                listeningStore.markPlayed(currentEpisode, currentPodcast);
            }
        });
        const unsubscribePlay = player.on("play", ({ state }) => {
            if (!playerStore.active || playerStore.isPlaying) {
                return;
            }

            playerStore.play(Math.floor(state.currentTime));
        });
        const unsubscribePause = player.on("pause", ({ state }) => {
            saveProgress(state, true);
            if (!playerStore.active || !playerStore.isPlaying) {
                return;
            }

            playerStore.pause(Math.floor(state.currentTime));
        });

        playerStore.restorePersistedSession();
        void persistence
            .restore()
            .then((restored) => {
                if (!restored) {
                    playerStore.close();
                }
            })
            .catch((error) => {
                console.error("Failed to restore persisted audio state", error);
                persistence.clear();
                playerStore.close();
            });

        return () => {
            unsubscribe();
            unsubscribeTime();
            unsubscribeEnded();
            unsubscribePlay();
            unsubscribePause();
            persistence.destroy();
            mediaSession.destroy();
            player.destroy();
        };
    }, [player]);

    const audioState = useMemo<AudioState>(
        () => ({
            audio: playerState.currentSource ?? undefined,
            isPlaying: playerState.status === "playing",

            setAudio(source, startAt) {
                resuming.current = !!startAt && startAt > 0;
                void player
                    .load(source)
                    .then(() => {
                        if (startAt && startAt > 0) {
                            player.seek(startAt);
                        }
                    })
                    .catch((error) => {
                        console.error("Failed to load audio source", error);
                    })
                    .finally(() => {
                        resuming.current = false;
                    });
            },

            play() {
                void player.play().catch((error) => {
                    console.error("Failed to start audio playback", error);
                    playerStore.setPlayingState(false);
                });
            },

            pause() {
                player.pause();
            },

            seek(time) {
                if (time !== undefined) {
                    player.seek(time);
                }

                return player.getState().currentTime;
            },

            duration() {
                return player.getState().duration;
            },

            rate(speed) {
                if (speed !== undefined) {
                    player.setRate(speed);
                    return speed;
                }

                return player.getState().rate;
            },

            unload() {
                player.unload();
            },
        }),
        [player, playerState.currentSource, playerState.rate, playerState.status]
    );

    return <AudioContext.Provider value={audioState}>{children}</AudioContext.Provider>;
};
